import React, { useState } from 'react';
import { Box, TextField, Button, CircularProgress, Switch, FormControlLabel, Typography, IconButton } from '@mui/material';
import { useTheme } from '../context/ThemeContext';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AttachedFiles from './AttachedFiles';

const ChatInput = ({
  input,
  onInputChange,
  isLoading,
  onSend,
  onStop,
  onFileUpload,
  fileInputRef,
  inputRef,
  useKnowledgeBase,
  setUseKnowledgeBase,
  searchEnabled,
  setSearchEnabled,
  attachedFiles,
  onFilesAttach,
  onFileRemove,
  sessionIdRef
}) => {
  const { theme } = useTheme();
  const [isDragging, setIsDragging] = useState(false);

  const handleSend = () => {
    if (input.trim() === '') return;
    onSend(null, useKnowledgeBase);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onFilesAttach(files, sessionIdRef?.current);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      onFilesAttach(files, sessionIdRef?.current);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onFilesAttach(files, sessionIdRef?.current);
    }
    e.target.value = '';
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 0
    }}>
      {attachedFiles && attachedFiles.length > 0 && (
        <AttachedFiles files={attachedFiles} onRemove={onFileRemove} />
      )}
      
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          borderRadius: '12px', 
          p: 1,
          border: '2px solid',
          borderColor: isDragging ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
          bgcolor: isDragging ? 'rgba(99, 102, 241, 0.05)' : 'background.paper',
          transition: 'all 0.2s',
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <IconButton
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          sx={{
            minWidth: 'auto',
            p: 1,
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}1A`,
              color: 'primary.main'
            },
          }}
        >
          <AttachFileIcon />
        </IconButton>

        <TextField
          fullWidth
          variant="outlined"
          multiline
          minRows={1}
          maxRows={10}
          placeholder={
            isLoading
              ? "Generating response..."
              : isDragging
              ? "Drop files here..."
              : "Ask me anything..."
          }
          value={input}
          onChange={onInputChange}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!isLoading) {
                handleSend();
              }
            }
          }}
          inputRef={inputRef}
          inputProps={{
            spellCheck: true,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'background.default',
              height: 'auto',
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-input': {
              py: 0.25,
              px: 2
            },
          }}
        />
        
        {isLoading ? (
          <IconButton
            onClick={onStop}
            sx={{
              minWidth: 'auto',
              p: 1,
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              },
            }}
          >
            <StopIcon />
          </IconButton>
        ) : (
          <Button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            sx={{
              minWidth: 'auto',
              p: 1,
              color: input.trim() ? theme.palette.primary.main : 'text.primary',
              '&:hover': {
                backgroundColor: `${theme.palette.primary.main}1A`,
              },
            }}
          >
            <SendIcon />
          </Button>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx"
        />
      </Box>
      
      {/* Toggles - Below input box */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 2,
        px: 1.5,
        pt: 1
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={useKnowledgeBase}
              onChange={(e) => setUseKnowledgeBase(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    color: 'primary.main',
                    '& + .MuiSwitch-track': {
                      bgcolor: 'primary.main',
                      opacity: 0.5,
                    }
                  }
                },
                '& .MuiSwitch-track': {
                  bgcolor: 'grey.500',
                  opacity: 0.3,
                }
              }}
            />
          }
          label={
            <Typography 
              variant="body2" 
              sx={{
                fontSize: '0.8rem',
                fontWeight: 400,
                color: 'text.secondary',
                transition: 'color 0.2s',
                ...(useKnowledgeBase && {
                  color: 'primary.main',
                  fontWeight: 500
                })
              }}
            >
              Knowledge Base
            </Typography>
          }
          sx={{ m: 0, gap: 0.5 }}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={searchEnabled}
              onChange={(e) => setSearchEnabled(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    color: 'primary.main',
                    '& + .MuiSwitch-track': {
                      bgcolor: 'primary.main',
                      opacity: 0.5,
                    }
                  }
                },
                '& .MuiSwitch-track': {
                  bgcolor: 'grey.500',
                  opacity: 0.3,
                }
              }}
            />
          }
          label={
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.8rem',
                fontWeight: 400,
                color: 'text.secondary',
                transition: 'color 0.2s',
                ...(searchEnabled && {
                  color: 'primary.main',
                  fontWeight: 500
                })
              }}
            >
              Web Search
            </Typography>
          }
          sx={{ m: 0, gap: 0.5 }}
        />
      </Box>
    </Box>
  );
};

export default ChatInput;

